import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { Checkbox } from 'antd'
import { NameDto } from 'api/Common/Types'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Link } from 'components/Link'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import {
  fetchFilterAction,
  removeFilterAction,
  saveFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { SelectVersion } from '../SelectVersion'
import { getFeatureFlagFilterFields } from './featureFlagFields'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

export interface FeatureFlagVersion {
  version?: NameDto<string>
  isEnabled?: boolean
}

export interface FeatureFlag {
  id: string
  name: string
  isEnabledDefault: boolean
  versions: FeatureFlagVersion[]
}

export const FeatureFlags = () => {
  const crumbs = [{ title: 'Product - Feature Flags', link: links.productFeatureFlags.url }]
  const navigate = useNavigate()
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [deleteModal, setDeleteModal] = useState<undefined | FeatureFlag>()
  const [isVersionModalOpen, setVersionModalOpen] = useState<boolean>(false)

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>()

  const { gridState, gridActions } = useGridState()

  const { filters } = useMapStateToProps()
  const { fetchFilters, resetGridFilters } = useMapDispatchToProps()

  useEffect(() => {
    resetGridFilters()
    if (isEmpty(filters)) fetchFilters()
  }, [])

  useEffect(() => {
    fetch()
  }, [
    gridState.pageSize,
    gridState.pageIndex,
    gridState.take,
    tableData?.skipCount,
    gridState.sort.length,
  ])

  const fetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = gridState.skip ?? 0
      const skipCount = tableData?.skipCount !== false

      const response = await clientAreaApiClient.getFeatureFlags({
        search,
        pageIndex,
        pageSize,
        skipCount,
      })

      if (response?.items) setFeatureFlags(response.items)
      else setFeatureFlags([])

      gridActions.setGridFilters({ take, skip, total: response.totalResults, pageIndex })
      setTableData({ ...tableData, meta: response })
    },
    [gridState, tableData]
  )

  const addNewFeatureFlag = async () => navigate('/product/feature-flags/new')

  const deleteFeatureFlag = async (id?: string) => {
    try {
      if (!id) return
      await clientAreaApiClient.deleteFeatureFlag(id)
      setToast(successToast('Feature Flag deleted successfully'))
      await fetch()
    } catch (_) {
      setToast(errorToast('Error deleting Feature Flag'))
    } finally {
      setDeleteModal(undefined)
    }
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (featureFlags === undefined) return null
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    gridActions.setGridFilters({ ...gridState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    gridActions.setGridFilters({ pageIndex })

  const handleVersionModalClose = () => setVersionModalOpen(false)

  const handleSelectedVersionFilter = (version: NameDto<string>) => {
    gridActions.addGridFiltersByFilterId({
      operator: '==',
      field: 'Version',
      value: version,
    })
    handleVersionModalClose()
  }

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={fetch}
        filterType={FilterType.FeatureFlags}
        gridState={gridState}
        filterFields={getFeatureFlagFilterFields()}
      />

      {!!deleteModal && (
        <ConfirmationModal
          contentText={`Are you sure you want to delete the ${deleteModal?.name} Feature Flag?`}
          yesButtonText='Yes, delete'
          onYes={() => deleteFeatureFlag(deleteModal?.id)}
          onCancel={() => setDeleteModal(undefined)}
          visible={!!deleteModal}
        />
      )}

      {isVersionModalOpen && (
        <Modal width={900} visible={isVersionModalOpen} onClose={handleVersionModalClose}>
          <SelectVersion onChange={handleSelectedVersionFilter} />
        </Modal>
      )}

      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditFeatureFlags)}
        text='Add New Feature Flag'
        onClick={addNewFeatureFlag}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={featureFlags}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          <GridColumn
            width={100}
            title='Enabled'
            field='isEnabledDefault'
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <Checkbox disabled checked={gridCellProps.dataItem.isEnabledDefault} />
              </td>
            )}
          />
          <GridColumn
            title='Feature Flag'
            field='name'
            cell={(props: GridCellProps) => (
              <td data-test='name-column'>
                <Link
                  to={{
                    pathname: generatePath(links.productFeatureFlags.detailsUrl as string, {
                      id: props.dataItem.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {props.dataItem.name}
                </Link>
              </td>
            )}
          />
          <GridColumn
            width={50}
            title=''
            cell={({ dataItem }: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      disabled: !hasPermission(permissions, PermissionsEnum.CanEditFeatureFlags),
                      onClick: () => setDeleteModal(dataItem),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}
